import React, { useEffect } from 'react';

import { useQueryParams } from 'utils/hooks/useQueryParams';
import { useTranslations } from 'utils/hooks/useTranslations';

import { BFS_DOI_TOKEN_QUERY_KEY, BfsDoiResultStatus } from '../BfsDoubleOptIn.model';
import BfsDoiResultPage from '../components/BfsDoiResultPage';
import { useWithdrawBfsConsent } from '../hooks/useWithdrawBfsConsent';

/**
 * Landing page for the withdrawal (Widerruf) link in the BfS marketing email
 * (EBS-64). Reads the token from the URL, revokes the marketing consent on
 * mount, and shows the result. Without a token the link is treated as invalid.
 *
 * There is no confirm button: the BE only accepts a client-side PATCH and
 * rejects GET, so email-client link previews cannot trigger the withdrawal.
 */
const BfsConsentWithdrawalPage = () => {
  const t = useTranslations('pages.bfsWithdrawal');
  const token = useQueryParams(BFS_DOI_TOKEN_QUERY_KEY) as string | null;

  const { mutate, isSuccess, isError } = useWithdrawBfsConsent();

  useEffect(() => {
    if (token) {
      mutate({ token });
    }
    // Fire once on mount with the token carried by the email link.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const status: BfsDoiResultStatus = (() => {
    if (!token || isError) {
      return 'error';
    }
    if (isSuccess) {
      return 'success';
    }
    return 'loading';
  })();

  const isSuccessful = status === 'success';

  return (
    <BfsDoiResultPage
      status={status}
      heading={isSuccessful ? t('success.heading') : t('error.heading')}
      subheading={isSuccessful ? t('success.subheading') : t('error.subheading')}
    />
  );
};

export default BfsConsentWithdrawalPage;
